import { action, thunk, persist, thunkOn } from 'easy-peasy';
import callApi, { generateUrlParams } from '../helpers/ApiAction';
import moment from 'moment';

const state = {
  builds: {
    list: [],
    pagination: {
      per_page: 10,
      current_page: 1,
      last_page: 1,
      total: 0
    },
    loading: false,
  },
  loadingBuilds: [],
  groups: {
    list: [],
    pagination: {
      per_page: 10,
      current_page: 1,
      last_page: 1,
      total: 0
    }
  },
};

const selectors = {
  buildsList: state.builds.list
};

const actions = {
  setLoadingBuilds: action((state, payload) => {
    state.builds.loading = payload;
  }),
  fetchBuilds: thunk(async (actions, payload) => {
    actions.setLoadingBuilds(true);
    const res = await callApi({
      endpoint: `builds${generateUrlParams(payload)}`,
      method: 'GET'
    });

    const json = await res.json();

    actions.setBuildList(json);
  }),
  onFetchedBuilds: thunkOn(
    actions => [actions.fetchBuilds],
    (actions) => {
      actions.setLoadingBuilds(false);
    }
  ),
  setLoadingBuild: action((state, payload) => {
    if (payload.loading) {
      state.loadingBuilds.push(payload.uuid);
    } else {
      state.loadingBuilds = state.loadingBuilds.filter(uuid => uuid !== payload.uuid);
    }
  }),
  fetchBuild: thunk(async (actions, payload) => {
    actions.setLoadingBuild({ uuid: payload, loading: true });
    const res = await callApi({
      endpoint: `builds/${payload}`,
      method: 'GET'
    });

    const json = await res.json();

    actions.updateBuild(json);
  }),
  onFetchedBuild: thunkOn(
    actions => [actions.fetchBuild],
    (actions, { payload }) => {
      actions.setLoadingBuild({ uuid: payload, loading: false });
    }
  ),
  uploadBuildForTest: thunk(async (actions, payload) => {
    const res = await callApi({
      endpoint: `builds/${payload.uuid}/uploadTestBuild`,
      method: 'POST',
      callbacks: payload.callbacks
    });

    const json = await res.json();

    actions.updateBuild(json);
  }),
  triggerTest: thunk(async (actions, payload) => {
    const res = await callApi({
      endpoint: `builds/${payload.uuid}/triggerTest`,
      method: 'POST',
      body: {
        ref: payload.ref,
        build_name: payload.buildName,
        test_name: payload.testName ?? `Manual trigger -> ${moment().format()}`
      },
      callbacks: payload.callbacks
    });

    return await res.json();
  }),
  cancelBuild: thunk(async (actions, payload) => {
    const { uuid, callbacks } = payload;
    
    const res = await callApi({
      endpoint: `builds/${uuid}`,
      method: 'DELETE',
      callbacks,
    });

    return await res.json();
  }),
  updateBuild: action((state, payload) => {
    const {
      data: { build }
    } = payload;
    const buildIndex = state.builds.list.findIndex(
      ({ uuid }) => uuid === build.uuid
    );

    if (buildIndex !== -1) {
      state.builds.list[buildIndex] = build;
    } else {
      state.builds.list.push(build);
    }
  }),
  setBuildList: action((state, payload) => {
    const {
      data: { builds },
      pagination
    } = payload;

    const { current_page, per_page } = pagination;

    if (current_page === 1 || per_page === 0) {
      // reset array so it's just builds
      state.builds.list = [];
    }
    
    state.builds.list.push(...builds);

    state.builds.pagination = pagination;
  }),
  fetchGroupBuilds: thunk(async (actions, payload) => {
    const res = await callApi({
      endpoint: `builds/groups`,
      method: 'GET'
    });

    const json = await res.json();

    actions.setGroupBuildsList(json);
  }),
  setGroupBuildsList: action((state, payload) => {
    const {
      data: { groups },
      pagination
    } = payload;

    const { current_page, per_page } = pagination;

    if (current_page === 1 || per_page === 0) {
      // reset array so it's just groups
      state.groups.list = [];
    }
    
    state.groups.list.push(...groups);

    state.groups.pagination = pagination;
  })
};

export default persist({
  ...state,
  ...selectors,
  ...actions
});
